import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { workScheduleService, appointmentService, settingsService, scheduleBlocksService } from '../services/api';

/**
 * Hook para calcular horários disponíveis baseado nas jornadas de trabalho e agendamentos existentes
 * Includes buffer time between appointments for travel/displacement (configurável)
 */
export const useAvailableTimeSlots = (userId, date, excludeAppointmentId = null) => {
  console.log('🔍 useAvailableTimeSlots called:', { userId, date, excludeAppointmentId });

  // Buscar configurações de apresentação
  const { data: presentationSettings } = useQuery({
    queryKey: ['presentation-settings'],
    queryFn: () => settingsService.getPresentationSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Buffer time configurável (padrão 30 minutos se não configurado)
  const BUFFER_MINUTES = presentationSettings?.data?.presentation_buffer_minutes
    ? parseInt(presentationSettings.data.presentation_buffer_minutes)
    : 30;

  // Duração configurável (padrão 60 minutos se não configurado)
  const DEFAULT_DURATION = presentationSettings?.data?.presentation_duration_minutes
    ? parseInt(presentationSettings.data.presentation_duration_minutes)
    : 60;

  // Buscar jornadas de trabalho do usuário
  const { data: schedulesData, isLoading: isLoadingSchedules } = useQuery({
    queryKey: ['work-schedules', userId],
    queryFn: () => {
      console.log('📅 Buscando jornadas de trabalho para usuário:', userId);
      return workScheduleService.getWorkSchedulesByUser(userId, { is_active: true });
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    onSuccess: (data) => {
      console.log('✅ Jornadas carregadas:', data);
    },
    onError: (error) => {
      console.error('❌ Erro ao carregar jornadas:', error);
    },
  });

  // Utilitário para criar Date local a partir de 'YYYY-MM-DD'
  const createLocalDateFromYmd = (ymd) => {
    if (!ymd) return null;
    const [year, month, day] = ymd.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Utilitário para converter appointment_date para Date local sem distorção de fuso
  const parseAppointmentToLocalDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hour, min] = match.map(Number);
      return new Date(year, month - 1, day, hour, min, 0, 0);
    }
    return new Date(dateStr);
  };

  // Utilitário para construir strings de data no fuso horário de Brasília (UTC-3)
  const toBrazilDateTimeString = (year, month, day, hour, minute, second) => {
    const yyyy = String(year).padStart(4, '0');
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const hh = String(hour).padStart(2, '0');
    const mi = String(minute).padStart(2, '0');
    const ss = String(second).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
  };

  // Buscar agendamentos do dia
  const { data: appointmentsData, isLoading: isLoadingAppointments } = useQuery({
    queryKey: ['appointments-day', userId, date],
    queryFn: () => {
      if (!date || !userId) return { data: [] };

      console.log('📅 Buscando agendamentos para:', { userId, date });
      const dateParts = date.split('-');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);

      return appointmentService.getAppointments({
        responsible_user_id: userId,
        date_from: toBrazilDateTimeString(year, month, day, 0, 0, 0),
        date_to: toBrazilDateTimeString(year, month, day, 23, 59, 59),
        limit: 100
      });
    },
    enabled: !!userId && !!date,
    staleTime: 30000,
    onSuccess: (data) => {
      console.log('✅ Agendamentos carregados:', data);
    },
    onError: (error) => {
      console.error('❌ Erro ao carregar agendamentos:', error);
    },
  });

  // Buscar bloqueios de agenda do dia
  const { data: blocksData, isLoading: isLoadingBlocks } = useQuery({
    queryKey: ['schedule-blocks-day', userId, date],
    queryFn: () => {
      if (!date || !userId) return { data: [] };

      return scheduleBlocksService.getBlocks({
        user_id: userId,
        date_from: date,
        date_to: date
      });
    },
    enabled: !!userId && !!date,
    staleTime: 60000,
  });

  // Calcular horários disponíveis
  const availableSlots = useMemo(() => {
    console.log('🔄 Calculando horários disponíveis:', { userId, date, schedulesData, appointmentsData });

    if (!userId || !date || !schedulesData?.data) {
      console.log('❌ Retornando vazio: !userId || !date || !schedulesData?.data', { userId, date, hasSchedules: !!schedulesData?.data });
      return [];
    }

    const selectedDate = createLocalDateFromYmd(date);
    if (!selectedDate) {
      console.log('❌ Retornando vazio: !selectedDate');
      return [];
    }

    // Verificar se a data é no passado (usando hora local)
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      console.log('❌ Data no passado - retornando vazio', { selectedDate, today });
      return [];
    }

    const dayOfWeek = selectedDate.getDay(); // 0 = Domingo, 6 = Sábado
    console.log('📅 Dia da semana:', dayOfWeek, '(0=Domingo, 6=Sábado)');

    // Encontrar jornada para o dia da semana
    const schedules = Array.isArray(schedulesData.data)
      ? schedulesData.data
      : Array.isArray(schedulesData.data?.data)
      ? schedulesData.data.data
      : [];

    console.log('📋 Jornadas encontradas:', schedules.length, schedules);

    const daySchedule = schedules.find(s => s.day_of_week === dayOfWeek && s.is_active);

    console.log('📅 Jornada do dia:', daySchedule);

    if (!daySchedule) {
      console.log('❌ Sem jornada para este dia - retornando vazio');
      return []; // Sem jornada para este dia
    }

    // Obter agendamentos do dia
    const appointments = Array.isArray(appointmentsData?.data)
      ? appointmentsData.data
      : Array.isArray(appointmentsData?.data?.data)
      ? appointmentsData.data.data
      : [];

    console.log('📅 Agendamentos do dia:', appointments.length, appointments);

    // Filtrar agendamentos excluindo o que está sendo editado
    // Incluir 30 min de buffer após cada agendamento
    const dayAppointments = appointments
      .filter(apt => {
        if (apt.status === 'CANCELLED' || apt.status === 'REJECTED') return false;
        const aptDate = parseAppointmentToLocalDate(apt.appointment_date);
        if (!aptDate) return false;
        const isSameDay =
          aptDate.getFullYear() === selectedDate.getFullYear() &&
          aptDate.getMonth() === selectedDate.getMonth() &&
          aptDate.getDate() === selectedDate.getDate();
        const isNotExcluded = !excludeAppointmentId || apt.id !== parseInt(excludeAppointmentId);
        return isSameDay && isNotExcluded;
      })
      .map(apt => {
        const aptStart = parseAppointmentToLocalDate(apt.appointment_date);
        const duration = parseInt(apt.duration_minutes, 10) || 60;
        const aptEnd = new Date(aptStart.getTime() + duration * 60000);
        // Adicionar buffer de 30 min após o fim do agendamento
        const bufferedEnd = new Date(aptEnd.getTime() + BUFFER_MINUTES * 60000);
        return {
          start: aptStart,
          end: aptEnd,
          bufferedEnd
        };
      });

    console.log('📅 Agendamentos com buffer de 30min:', dayAppointments.map(a => ({
      start: a.start.toLocaleTimeString('pt-BR'),
      end: a.end.toLocaleTimeString('pt-BR'),
      bufferedEnd: a.bufferedEnd.toLocaleTimeString('pt-BR')
    })));

    // Obter bloqueios do dia
    const blocks = Array.isArray(blocksData?.data)
      ? blocksData.data
      : Array.isArray(blocksData?.data?.data)
      ? blocksData.data.data
      : [];

    const dayBlocks = blocks.filter(block => {
      const blockDateOnly = block.block_date ? block.block_date.split('T')[0] : null;
      return blockDateOnly === date;
    });

    const hasFullDayBlock = dayBlocks.some(block => !block.start_time && !block.end_time);

    console.log('🔍 BLOCK CHECK em availableSlots: hasFullDayBlock:', hasFullDayBlock, 'dayBlocks:', dayBlocks.length);

    // Converter horários de trabalho para objetos Date
    const [startHour, startMin] = daySchedule.start_time.split(':').map(Number);
    const [endHour, endMin] = daySchedule.end_time.split(':').map(Number);
    
    const workStart = new Date(selectedDate);
    workStart.setHours(startHour, startMin, 0, 0);
    
    const workEnd = new Date(selectedDate);
    workEnd.setHours(endHour, endMin, 0, 0);

    // Considerar intervalo de almoço se existir
    let breakStart = null;
    let breakEnd = null;
    if (daySchedule.break_start_time && daySchedule.break_end_time) {
      const [breakStartHour, breakStartMin] = daySchedule.break_start_time.split(':').map(Number);
      const [breakEndHour, breakEndMin] = daySchedule.break_end_time.split(':').map(Number);
      
      breakStart = new Date(selectedDate);
      breakStart.setHours(breakStartHour, breakStartMin, 0, 0);
      
      breakEnd = new Date(selectedDate);
      breakEnd.setHours(breakEndHour, breakEndMin, 0, 0);
    }

    // Gerar slots de 15 em 15 minutos
    // Considerar buffer configurável para durações típicas
    const slots = [];
    const slotDuration = 15; // minutos
    const minDuration = DEFAULT_DURATION; // duração configurável
    const minTotalTime = minDuration + BUFFER_MINUTES; // duração + buffer
    let currentTime = new Date(workStart);

    while (currentTime < workEnd) {
      const slotEndPlusBuffer = new Date(currentTime.getTime() + minTotalTime * 60000);

      // Se houver bloqueio de dia inteiro, não adiciona nenhum slot
      if (hasFullDayBlock) {
        break;
      }

      // Verificar se o slot está dentro do intervalo de almoço
      const isInBreak = breakStart && breakEnd &&
                       currentTime < breakEnd &&
                       slotEndPlusBuffer > breakStart;

      // Verificar se o slot conflita com algum agendamento (considerando buffer de 30min)
      const hasConflict = dayAppointments.some(apt => {
        return (currentTime >= apt.start && currentTime < apt.bufferedEnd) ||
               (slotEndPlusBuffer > apt.start && slotEndPlusBuffer <= apt.bufferedEnd) ||
               (currentTime <= apt.start && slotEndPlusBuffer >= apt.bufferedEnd);
      });

      // Verificar se o slot conflita com algum bloqueio parcial
      const hasBlockConflict = dayBlocks.some(block => {
        if (!block.start_time || !block.end_time) return false;
        const blockStartStr = block.start_time.substring(0, 5);
        const blockEndStr = block.end_time.substring(0, 5);
        const slotStartStr = currentTime.toTimeString().substring(0, 5);
        const slotEndStr = slotEndPlusBuffer > workEnd
          ? `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`
          : slotEndPlusBuffer.toTimeString().substring(0, 5);
        // Conflito se os intervalos se sobrepõem
        return (slotStartStr < blockEndStr && slotEndStr > blockStartStr);
      });

      if (!isInBreak && !hasConflict && !hasBlockConflict && slotEndPlusBuffer <= workEnd) {
        slots.push({
          time: new Date(currentTime),
          formatted: currentTime.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
          })
        });
      }

      currentTime = new Date(currentTime.getTime() + slotDuration * 60000);
    }

    return slots;
  }, [userId, date, schedulesData, appointmentsData, blocksData, excludeAppointmentId]);

  // Verificar se um horário específico está disponível
  const isTimeSlotAvailable = (time, durationMinutes = DEFAULT_DURATION) => {
    console.log('🔍 isTimeSlotAvailable called:', { time, durationMinutes, userId, date, blocksDataAvailable: !!blocksData });

    // Se não tiver usuário ou data, retorna true (não blocking)
    if (!userId || !date) {
      console.log('⚠️ returning true: !userId || !date', { userId, date });
      return true;
    }

    // Se o horário não foi definido, retorna true
    if (!time) return true;

    const appointmentStart = parseAppointmentToLocalDate(time);
    // Se a data do agendamento for inválida, retorna true
    if (!appointmentStart || isNaN(appointmentStart.getTime())) return true;

    const appointmentEnd = new Date(appointmentStart.getTime() + durationMinutes * 60000);

    // Verificar se está dentro do horário de trabalho
    const selectedDate = createLocalDateFromYmd(date);
    if (!selectedDate) return true; // Retorna true se não tiver data
    const dayOfWeek = selectedDate.getDay();

    const schedules = Array.isArray(schedulesData?.data)
      ? schedulesData.data
      : Array.isArray(schedulesData?.data?.data)
      ? schedulesData.data.data
      : [];

    // Se schedules ainda não carregaram, retorna true (não bloqueia)
    if (!schedulesData || schedules.length === 0) {
      console.log('⚠️ Schedules ainda não carregaram, retornando true');
      return true;
    }

    const daySchedule = schedules.find(s => s.day_of_week === dayOfWeek && s.is_active);
    // Se não houver jornada cadastrada para o dia, retorna true (não bloqueia)
    if (!daySchedule) {
      console.log('⚠️ Sem jornada para dia', dayOfWeek, '- retornando true');
      return true;
    }

    const [startHour, startMin] = daySchedule.start_time.split(':').map(Number);
    const [endHour, endMin] = daySchedule.end_time.split(':').map(Number);
    
    const workStart = new Date(selectedDate);
    workStart.setHours(startHour, startMin, 0, 0);

    const workEnd = new Date(selectedDate);
    workEnd.setHours(endHour, endMin, 0, 0);

    // Novo agendamento também precisa de buffer de 30min após seu fim
    const appointmentBufferedEnd = new Date(appointmentEnd.getTime() + BUFFER_MINUTES * 60000);

    if (appointmentStart < workStart || appointmentBufferedEnd > workEnd) {
      console.log('❌ Horário ultrapassa limite de trabalho:', {
        appointmentStart: appointmentStart.toLocaleTimeString('pt-BR'),
        appointmentBufferedEnd: appointmentBufferedEnd.toLocaleTimeString('pt-BR'),
        workEnd: workEnd.toLocaleTimeString('pt-BR')
      });
      return false;
    }

    // Verificar intervalo de almoço
    if (daySchedule.break_start_time && daySchedule.break_end_time) {
      const [breakStartHour, breakStartMin] = daySchedule.break_start_time.split(':').map(Number);
      const [breakEndHour, breakEndMin] = daySchedule.break_end_time.split(':').map(Number);

      const breakStart = new Date(selectedDate);
      breakStart.setHours(breakStartHour, breakStartMin, 0, 0);

      const breakEnd = new Date(selectedDate);
      breakEnd.setHours(breakEndHour, breakEndMin, 0, 0);

      // Log para debug
      console.log('🔍 Break check:', {
        appointmentStart: appointmentStart.toLocaleTimeString('pt-BR'),
        appointmentEnd: appointmentEnd.toLocaleTimeString('pt-BR'),
        breakStart: breakStart.toLocaleTimeString('pt-BR'),
        breakEnd: breakEnd.toLocaleTimeString('pt-BR'),
        overlaps: (appointmentStart < breakEnd && appointmentEnd > breakStart)
      });

      // Apenas retornar false se o agendamento INTERSECTAR o intervalo de almoço
      if ((appointmentStart < breakEnd && appointmentEnd > breakStart)) {
        console.log('❌ Bloqueado por intervalo de almoço');
        return false;
      }
    }

    // Verificar conflitos com agendamentos existentes (considerando buffer de 30min)
    const appointments = Array.isArray(appointmentsData?.data)
      ? appointmentsData.data
      : Array.isArray(appointmentsData?.data?.data)
      ? appointmentsData.data.data
      : [];

    const dayAppointments = appointments
      .filter(apt => {
        if (apt.status === 'CANCELLED' || apt.status === 'REJECTED') return false;
        const aptDate = parseAppointmentToLocalDate(apt.appointment_date);
        if (!aptDate) return false;
        const isSameDay =
          aptDate.getFullYear() === selectedDate.getFullYear() &&
          aptDate.getMonth() === selectedDate.getMonth() &&
          aptDate.getDate() === selectedDate.getDate();
        const isNotExcluded = !excludeAppointmentId || apt.id !== parseInt(excludeAppointmentId);
        return isSameDay && isNotExcluded;
      })
      .map(apt => {
        const aptStart = parseAppointmentToLocalDate(apt.appointment_date);
        const duration = parseInt(apt.duration_minutes, 10) || 60;
        const aptEnd = new Date(aptStart.getTime() + duration * 60000);
        // Adicionar buffer de 30 min após o fim do agendamento
        const bufferedEnd = new Date(aptEnd.getTime() + BUFFER_MINUTES * 60000);
        return {
          start: aptStart,
          end: aptEnd,
          bufferedEnd
        };
      });

    const hasConflict = dayAppointments.some(apt => {
      return (appointmentStart >= apt.start && appointmentStart < apt.bufferedEnd) ||
             (appointmentEnd > apt.start && appointmentEnd <= apt.bufferedEnd) ||
             (appointmentStart <= apt.start && appointmentEnd >= apt.bufferedEnd);
    });

// Verificar conflitos com bloqueios de agenda
    let hasBlockConflict = false;
    let blockCheckError = null;

    try {
      const blocks = Array.isArray(blocksData?.data)
        ? blocksData.data
        : Array.isArray(blocksData?.data?.data)
        ? blocksData.data.data
        : [];

      console.log('🔍 BLOCK CHECK: blocksData structure:', JSON.stringify(blocksData, null, 2));
      console.log('🔍 BLOCK CHECK: blocks extracted:', blocks);
      console.log('🔍 BLOCK CHECK: date being used:', date);

      const dayBlocks = blocks.filter(block => {
        // O block_date do banco vem como ISO string (2026-05-21T00:00:00.000Z)
        // Precisamos extrair apenas a parte da data para comparar com a data local
        // Para evitar problemas de timezone, extraímos apenas YYYY-MM-DD
        const blockDateOnly = block.block_date ? block.block_date.split('T')[0] : null;
        const matches = blockDateOnly === date;
        console.log('🔍 BLOCK FILTER: comparing', blockDateOnly, '===', date, 'result:', matches);
        return matches;
      });
      console.log('🔍 BLOCK CHECK: dayBlocks after filter:', dayBlocks);

      // Se houver bloqueio de dia inteiro, não permite nenhum agendamento
      // Verificar também se não há bloqueios parciais que cubram o horário
      const hasFullDayBlock = dayBlocks.some(block =>
        !block.start_time && !block.end_time
      );
      console.log('🔍 BLOCK CHECK: hasFullDayBlock:', hasFullDayBlock);

      if (hasFullDayBlock) {
        console.log('❌ Bloqueado por bloqueio de dia inteiro');
        return false;
      }

      // Verificar bloqueios parciais
      hasBlockConflict = dayBlocks.some(block => {
        if (!block.start_time || !block.end_time) return false;

        const blockStart = block.start_time.substring(0, 5);
        const blockEnd = block.end_time.substring(0, 5);
        const aptStartStr = appointmentStart.toTimeString().substring(0, 5);
        const aptEndStr = appointmentEnd.toTimeString().substring(0, 5);

        const conflicts = (aptStartStr < blockEnd && aptEndStr > blockStart);
        console.log('🔍 BLOCK CHECK partial: block', block.id, 'apt', aptStartStr, '-', aptEndStr, 'block', blockStart, '-', blockEnd, 'conflicts:', conflicts);

        return conflicts;
      });
      console.log('🔍 BLOCK CHECK: hasBlockConflict:', hasBlockConflict);

      if (hasBlockConflict) {
        console.log('❌ Bloqueado por conflito de bloqueio parcial');
        return false;
      }
    } catch (err) {
      blockCheckError = err;
      console.error('❌ BLOCK CHECK ERROR:', err);
    }

console.log('🔍 isTimeSlotAvailable result:', {
      appointmentStart: appointmentStart.toLocaleTimeString('pt-BR'),
      appointmentEnd: appointmentEnd.toLocaleTimeString('pt-BR'),
      hasConflict,
      hasBlockConflict,
      blockCheckError,
      dayAppointments: dayAppointments.map(a => ({
        start: a.start.toLocaleTimeString('pt-BR'),
        end: a.end.toLocaleTimeString('pt-BR'),
        bufferedEnd: a.bufferedEnd.toLocaleTimeString('pt-BR')
      }))
    });

    return !hasConflict && !hasBlockConflict;
  };

  return {
    availableSlots,
    isTimeSlotAvailable,
    isLoading: isLoadingSchedules || isLoadingAppointments || isLoadingBlocks,
    isLoadingSchedules,
    isLoadingAppointments,
    isLoadingBlocks
  };
};

